import { LanguageCode } from 'mytours-read-api-typescript'
import LocalizedStrings, { GlobalStrings } from 'react-localization'

const languageImports = import.meta.glob('./strings/strings-*.json', { eager: true })

// list of codes ISO 639 - https://www.loc.gov/standards/iso639-2/php/code_list.php
export const availableLanguages = [
	'alq',
	'ar',
	'bg',
	'buc',
	'ca',
	'cs',
	'cy',
	'da',
	'de',
	'el',
	'en-GB',
	'en',
	'es',
	'eu',
	'fa',
	'fi',
	'fj',
	'fo',
	'fr',
	'ga',
	'haw',
	'he',
	'hi',
	'hmn',
	'hr',
	'ht',
	'hu',
	'hy',
	'id',
	'is',
	'it',
	'iu',
	'ja',
	'ka',
	'kk',
	'ko',
	'lkt',
	'lld',
	'lo',
	'lt',
	'lv',
	'mh',
	'mi',
	'ms',
	'ne',
	'nl',
	'no',
	'oji',
	'pa',
	'pap',
	'pl',
	'pt-BR',
	'pt',
	'ro',
	'ru',
	'see',
	'sm',
	'so',
	'st',
	'sv',
	'swb',
	'ta',
	'th',
	'tl',
	'tn',
	'to',
	'tr',
	'uk',
	'vi',
	'xh',
	'zh-Hans',
	'zh-Hant',
	'zu',
]

export const languageStrings: GlobalStrings<Strings> = {}

Object.keys(languageImports).forEach(key => {
	const value = key.replace('./strings/strings-', '').replace('.json', '')
	languageStrings[value] = {
		...(languageImports[key] as Strings),
	}
})

export const languageCodes: LanguageCode[] = Object.keys(languageStrings) as LanguageCode[]

export default new LocalizedStrings<Strings>(languageStrings)

interface Strings {
	// lock app
	unlock_the_app: string
	unlock_the_collection: string
	enter_your_access_code_to_continue: string
	unlock: string
	buy_for: string
	subtotal: string
	tax: string
	total: string
	email: string
	email_label: string
	pay_now: string
	pay_x_amount: string
	code_invalid: string
	code_expired: string
	validating_payment: string
	validating_payment_failed: string
	purchase_successful: string
	purchase_successful_description: string
	purchase_successful_continue: string
	// tabs
	collections: string
	screens: string
	info: string
	information: string
	scan_and_find: string
	settings: string
	profile: string
	support: string
	keypad_entry_tab_label: string
	view: string
	view_in_android_app: string
	view_in_ios_app: string
	// scanner screen
	scan: string
	enter_code: string
	not_valid_qr_code: string
	camera_does_not_support: string
	do_not_have_camera_permissions: string
	// settings screen
	app_version: string
	app_id: string
	change_app_id: string
	enter_app_id: string
	enter_api_key: string
	qr_scanner: string
	update_metadata: string
	clear_quiz_results: string
	language: string
	languages: string
	select_language: string
	loading_meta_data: string
	checking_for_updates: string
	selected_language_loading: string

	// collections
	no_collections: string
	loading: string
	error_occurred: string
	network_error: string
	error_boundary_fallback_title: string
	error_boundary_fallback_refresh: string
	// collection
	search: string
	collection: string
	// Lock collection
	code_max_redemptions: string
	code_requires_email: string
	code_enter_purchase_email: string
	code_email_mismatch: string
	// Code redemption (URL-based)
	code_redemption_failed: string
	code_redemption_error: string
	code_redemption_confirm_title: string
	code_redemption_confirm_message: string
	code_redemption_confirm_button: string
	// Bundle redemption
	bundle_unlock_title: string
	bundle_available_in_bundles: string
	bundle_offer_buy: string
	bundle_offer_choose: string
	bundle_offer_choose_from: string
	bundle_offer_get: string
	bundle_offer_included_in: string
	bundle_offer_items: string
	bundle_offer_save: string
	bundle_offer_save_up_to: string
	bundle_offer_tours: string
	bundle_unlock_description: string
	bundle_redeem_button: string
	bundle_redeem_title: string
	bundle_redeem_description: string
	bundle_redeem_input_placeholder: string
	bundle_contents_title: string
	bundle_contents_empty: string
	bundle_contents_close: string
	bundle_buy_button: string
	bundle_buy_title: string
	bundle_buy_description: string
	bundle_pay_button: string
	bundle_purchase_success_title: string
	bundle_purchase_success_description: string
	bundle_scan_qr_button: string
	bundle_qr_scan_instruction: string
	bundle_qr_wrong_item: string
	connection_error_message: string
	copy: string
	copied: string
	// collection item
	collection_item_s: string
	// screen
	play: string
	pause: string
	play_media: string
	pause_media: string
	no_content_available: string
	back: string
	links: string
	transcript: string
	transcript_available: string
	captions: string
	next_audio_track_s: string
	ok: string
	retry: string
	finish: string
	close: string
	back_to_map: string
	accessibility_finish_audio_track: string
	accessibility_go_back: string
	accessibility_seek_forward: string
	accessibility_seek_backward: string
	accessibility_audio_seek: string
	accessibility_video_seek: string
	accessibility_open_fullscreen_media: string
	accessibility_open_fullscreen_player: string
	accessibility_close_fullscreen_player: string
	accessibility_open_transcript: string
	accessibility_close_transcript: string
	accessibility_open_stop_list: string
	accessibility_play_audio_s: string
	accessibility_playback_speed: string
	accessibility_pause_audio_s: string
	accessibility_previous: string
	accessibility_next: string
	accessibility_play_again: string
	accessibility_remove: string
	accessibility_show_map_layers: string
	accessibility_input: string
	free_text_answer_input_placeholder: string
	quiz_score_section_title: string
	quiz_score_section_score_label: string
	// tour
	distance_meters: string
	distance_meters_s: string
	distance_kilometers: string
	distance_kilometers_s: string
	distance_miles: string
	distance_miles_s: string
	distance_feet: string
	distance_feet_s: string
	hrs: string
	stops: string
	stop_number: string
	no_stops: string
	tour_details_title: string
	start_tour: string
	load_tour: string
	loading_tour: string
	downloading_content: string
	download: string
	download_tour: string
	downloading_tour: string
	downloaded_tour: string
	download_incomplete: string
	download_new_language: string
	remove_download: string
	remove_all_downloads: string
	start_anyway: string
	available_online: string
	needs_connection: string
	offline_banner: string
	offline_storage: string
	offline_storage_used: string
	tour_previous: string
	tour_next: string
	tour_exit: string
	open_details: string
	open_menu: string
	open_tour_stop: string
	duration_days_and_hours: string
	duration_days_and_hour: string
	duration_days: string
	duration_hours_and_minutes: string
	duration_hours: string
	duration_minutes: string
	duration_day_and_hours: string
	duration_day_and_hour: string
	duration_day: string
	duration_hour_and_minutes: string
	duration_hour: string
	distance_km: string
	n_of_total: string
	keypad_entry_error: string
	keypad_entry_heading: string
	results: string
	view_stop: string
	open_map: string
	close_map: string
	center_map: string
	cancel: string
	free: string
	get_directions: string
	directions: string
	my_location: string
	floor: string
	// info
	info_title: string
	info_page_title: string
	// support
	support_title: string
	support_error_message: string
	// map
	route_cycling: string
	route_walking: string
	route_driving: string
	route_distance: string
	max_elevation: string
	distance: string
	elevation: string

	// languages
	alq: string
	ar: string
	bg: string
	ca: string
	cs: string
	cy: string
	da: string
	de: string
	el: string
	en_gb: string
	en: string
	es: string
	eu: string
	fa: string
	fi: string
	fj: string
	fo: string
	fr: string
	ga: string
	haw: string
	he: string
	hi: string
	hmn: string
	hr: string
	ht: string
	hu: string
	hy: string
	id: string
	is: string
	it: string
	iu: string
	ja: string
	ka: string
	kk: string
	ko: string
	lkt: string
	lld: string
	lo: string
	lt: string
	lv: string
	mh: string
	mi: string
	ms: string
	ne: string
	nl: string
	no: string
	oji: string
	pa: string
	pap: string
	pl: string
	pt_br: string
	pt: string
	ro: string
	ru: string
	see: string
	sm: string
	so: string
	st: string
	sv: string
	ta: string
	th: string
	tl: string
	tn: string
	to: string
	tr: string
	uk: string
	vi: string
	xh: string
	zh_hans: string
	zh_hant: string
	zu: string

	permission_modify_camera_title: string
	permission_modify_location_title: string
	camera_access_needed: string
	location_access_needed: string
	do_not_show_me_again: string
	share: string
	expand_map: string

	submit: string
	try_again: string
	clear_quiz_results_confirmation_text: string
	page_not_found_title: string
	page_not_found_text: string

	//Social Media
	follow_us_on_facebook: string

	// Tour Types
	tour_type_4wd: string
	tour_type_aboriginal_site: string
	tour_type_canoeing: string
	tour_type_cycling: string
	tour_type_driving: string
	tour_type_gallery: string
	tour_type_historic_house: string
	tour_type_horse_trail: string
	tour_type_museum: string
	tour_type_scavenger_hunt: string
	tour_type_walking: string
	tour_type_ship: string
	tour_type_bus: string
	tour_type_airplane: string
	tour_type_helicopter: string
	tour_type_nature_trail: string

	collapse: string
	expand: string

	recently_earned_badges: string
	browse_all: string
	purchased_content: string
	badges: string
	profile_no_recently_earned_badges_text: string
	demo_mode_label: string

	recent_favourites: string
	favourites: string
	unfavourite: string
	favourite: string
	unfavourite_confirmation: string
	save_your_favourites: string
	save_your_favourites_desc: string
	show_more: string

	live_tours_search: string
	search_description: string
	search_none_found: string
	search_tab_label: string

	// Form
	form_field_empty: string
	form_marketing_list_empty: string
	form_marketing_list_skippable_empty: string
	form_skip: string
}
